import { Delete, Pause, PauseCircle, PlayArrowOutlined, PlayCircleFilled, PlayCircleOutline, Start } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import {
    Box,
    Grid,
    IconButton,
    Paper,
    Tooltip,
    Typography,
    Button,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { safeDateFormat } from "qubCommon/helperMethods/dateHelper";
import { showFloat } from "qubCommon/wijmoHelpers/numberMethods";

const ApplicationTypeSeasonsTable = ({ rows = [], onEdit, onStartPause, onDelete }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    if (!rows || !rows.length) return null;

    if (isMobile) {
        // 📱 Mobile layout
        return (
            <Box sx={{ mt: 2 }}>
                {rows.map((row, idx) => (
                    <Paper
                        key={row.id || idx}
                        elevation={0}
                        sx={{
                            border: "1px solid #dcdcdc",
                            borderRadius: 1.5,
                            mb: 1.5,
                            p: 2,
                            backgroundColor: row.paused ? "#fff8f8" : "#fff",
                        }}
                    >
                        <Typography color="primary" fontWeight={700}>
                            {row.applicationType}
                        </Typography>



                        <Typography variant="body2" color="text.secondary">
                            Ποσό:{" "}
                            <Typography component="span" color="text.primary">
                                {row.amount}
                            </Typography>
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                            Όριο:{" "}
                            <Typography component="span" color="text.primary">
                                {row.limit}
                            </Typography>
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                            Από:{" "}
                            <Typography component="span" color="text.primary">
                                {safeDateFormat(row.dateFrom, 'dd/MM/yyyy HH:mm')}
                            </Typography>
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                            Έως:{" "}
                            <Typography component="span" color="text.primary">
                                {safeDateFormat(row.dateTo, 'dd/MM/yyyy HH:mm')}
                            </Typography>
                        </Typography>

                        {row.paused && (
                            <Typography
                                variant="body2"
                                sx={{ mt: 0.5, fontWeight: 600, color: "error.main" }}
                            >
                                Σε παύση
                            </Typography>
                        )}

                        {/* <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            sx={{
                                mt: 1.5,
                                alignSelf: "flex-end",
                                textTransform: "none",
                                borderRadius: 2,
                                fontWeight: 600,
                            }}
                            onClick={() => onEdit?.(row)}
                        >
                            Επεξεργασία
                        </Button> */}
                    </Paper>
                ))}
            </Box>
        );
    }

    // 🖥️ Desktop grid layout
    return (
        <Box
            sx={{
                border: "1px solid #dcdcdc",
                borderRadius: 1.5,
                overflow: "hidden",
                mt: 2,
            }}
        >
            {/* HEADER */}
            <Grid
                container
                sx={{
                    backgroundColor: "#f2f2f2",
                    borderBottom: "1px solid #dcdcdc",
                    py: 1,
                    px: 2,
                }}
            >
                <Grid item xs={2}>
                    <Typography fontWeight={600}>Είδος Αίτησης</Typography>
                </Grid>

                <Grid item xs={1}>
                    <Typography fontWeight={600}>Όριο</Typography>
                </Grid>

                <Grid item xs={1}>
                    <Typography fontWeight={600}>Ποσό (€)</Typography>
                </Grid>

                <Grid item xs={1}>
                    <Typography fontWeight={600}>Αρ. Πλ. ΔΙΑΣ</Typography>
                </Grid>

                <Grid item xs={2}>
                    <Typography fontWeight={600}>Κωδικοποίηση ΔΙΑΣ</Typography>
                </Grid>

                <Grid item xs={1}>
                    <Typography fontWeight={600}>Προμήθεια (€)</Typography>
                </Grid>

                <Grid item xs={3} align="center">
                    <Typography fontWeight={600}>Υποβολή αιτήσεων</Typography>
                </Grid>

                <Grid item xs={1}>
                    <Typography fontWeight={600} textAlign="right">
                        Ενέργεια
                    </Typography>
                </Grid>
            </Grid>

            {/* ROWS */}
            {rows.map((row, idx) => (
                <Grid
                    key={row.id || idx}
                    container
                    alignItems="center"
                    sx={{
                        backgroundColor: idx % 2 === 0 ? "#fafafa" : "#fff",
                        borderBottom: idx < rows.length - 1 ? "1px solid #e0e0e0" : "none",
                        py: 1.2,
                        px: 2,
                    }}
                >
                    <Grid item xs={2}>
                        <Typography color="primary" fontWeight={600}>
                            {row.applicationType}
                        </Typography>
                    </Grid>

                    <Grid item xs={1}>
                        <Typography>{row.limit}</Typography>
                    </Grid>

                    <Grid item xs={1}>
                        <Typography>{row.amount}</Typography>
                    </Grid>

                    <Grid item xs={1}>
                        <Typography>{row.diasCode}</Typography>
                    </Grid>

                    <Grid item xs={2}>
                        <Typography>{row.diasDataKey}</Typography>
                    </Grid>

                    <Grid item xs={1}>
                        <Typography>{row.diasFee} €</Typography>
                    </Grid>

                    <Grid item xs={3} align="center">
                        <Typography>
                            {safeDateFormat(row.dateFrom, "dd/MM/yyyy HH:mm")} –{" "}
                            {safeDateFormat(row.dateTo, "dd/MM/yyyy HH:mm")}
                        </Typography>
                    </Grid>

                    <Grid item xs={1} textAlign="right">
                        <Tooltip title="Επεξεργασία">
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={() => onEdit?.(row)}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        {row.showPaused && <Tooltip title={row.paused ? "Έναρξη" : "Παύση"}>
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={() => onStartPause?.(row)}
                            >
                                {row.paused ? <PlayCircleFilled color='success' fontSize="small" /> : <PauseCircle color='orange' fontSize="small" />}
                            </IconButton>
                        </Tooltip>}
                        <Tooltip title="Διαγραφή">
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => onDelete?.(row)}
                            >
                                <Delete fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            ))}
        </Box>

    );
};

export default ApplicationTypeSeasonsTable;
